import { Image, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { BoldText } from "../BoldText";

import { courseTheme } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

type Quiz = Extract<Screen, { kind: "quiz" }>;
const MARKS = ["A", "B", "C", "D"];

// Quiz screen — "try again until correct". A wrong pick is marked red and
// disabled and shows a "try again" nudge WITHOUT revealing the answer; the
// correct answer only turns green once she taps it (solved). Then the good
// explanation shows and the footer unlocks.

export function QuizScreen({
    screen,
    quizIndex,
    quizCount,
    selected,
    triedWrong,
    solved,
    onSelect
}: {
    screen: Quiz;
    /** 1-based position among this course's quizzes. */
    quizIndex: number;
    quizCount: number;
    selected: number | null;
    triedWrong: number[];
    solved: boolean;
    onSelect: (i: number) => void;
}) {
    const hasMissed = !solved && triedWrong.length > 0;
    return (
        <View>
            <View style={styles.qhead}>
                <Text style={styles.qnum}>{`Quick check · ${quizIndex} of ${quizCount}`}</Text>
                <View style={styles.qdots}>
                    {Array.from({ length: quizCount }).map((_, j) => {
                        const done = j < quizIndex - 1;
                        const cur = j === quizIndex - 1;
                        return (
                            <View
                                key={j}
                                style={[
                                    styles.qdot,
                                    done && styles.qdotDone,
                                    cur && styles.qdotCur
                                ]}
                            />
                        );
                    })}
                </View>
            </View>

            {screen.image ? (
                <View
                    style={[
                        styles.qimage,
                        { aspectRatio: screen.imageAspect === "3:4" ? 3 / 4 : 4 / 3 }
                    ]}
                >
                    <Image
                        source={{ uri: screen.image }}
                        style={StyleSheet.absoluteFill}
                        resizeMode="cover"
                    />
                </View>
            ) : null}

            <Text style={styles.qtext}>{screen.question}</Text>

            <View style={styles.opts}>
                {screen.options.map((opt, i) => {
                    const isWrong = triedWrong.includes(i);
                    const isSolvedCorrect = solved && i === screen.answer;
                    const isSel = selected === i && !solved;
                    const disabled = solved || isWrong;
                    return (
                        <TouchableOpacity
                            key={i}
                            activeOpacity={0.8}
                            disabled={disabled}
                            onPress={() => onSelect(i)}
                            style={[
                                styles.opt,
                                isSel && styles.optSel,
                                isSolvedCorrect && styles.optCorrect,
                                isWrong && styles.optWrong
                            ]}
                        >
                            <View
                                style={[
                                    styles.mark,
                                    isSel && styles.markSel,
                                    isSolvedCorrect && styles.markCorrect,
                                    isWrong && styles.markWrong
                                ]}
                            >
                                <Text
                                    style={[
                                        styles.markText,
                                        (isSel || isSolvedCorrect || isWrong) &&
                                            styles.markTextActive
                                    ]}
                                >
                                    {MARKS[i]}
                                </Text>
                            </View>
                            <Text style={[styles.optText, isWrong && styles.optTextWrong]}>
                                {opt}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {solved ? (
                <View style={[styles.explain, styles.explainGood]}>
                    <BoldText
                        text={screen.explainGood}
                        style={styles.explainText}
                        boldColor={courseTheme.navy}
                    />
                </View>
            ) : hasMissed ? (
                <View style={[styles.explain, styles.explainBad]}>
                    <Text style={styles.tryAgain}>Not quite — try again.</Text>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    qhead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
    qnum: { fontSize: 12, fontWeight: "700", color: courseTheme.tealD, letterSpacing: 0.3 },
    qdots: { flexDirection: "row", gap: 6 },
    qdot: { width: 7, height: 7, borderRadius: 99, backgroundColor: courseTheme.line },
    qdotDone: { backgroundColor: courseTheme.teal },
    qdotCur: { backgroundColor: courseTheme.tealD },
    qimage: {
        width: "100%",
        borderRadius: 14,
        overflow: "hidden",
        backgroundColor: "rgba(0,0,0,0.06)",
        marginBottom: 16
    },
    qtext: {
        fontSize: 18,
        fontWeight: "800",
        color: courseTheme.navy,
        marginTop: 14,
        lineHeight: 24
    },
    opts: { marginTop: 16, gap: 10 },
    opt: {
        flexDirection: "row",
        alignItems: "center",
        gap: 13,
        borderWidth: 1.5,
        borderColor: courseTheme.line,
        backgroundColor: courseTheme.white,
        borderRadius: 15,
        paddingVertical: 14,
        paddingHorizontal: 15
    },
    optSel: { borderColor: courseTheme.teal, backgroundColor: courseTheme.tealTint },
    optCorrect: { borderColor: courseTheme.green, backgroundColor: courseTheme.greenTint },
    optWrong: { borderColor: courseTheme.red, backgroundColor: courseTheme.redTint },
    mark: {
        width: 24,
        height: 24,
        borderRadius: 7,
        borderWidth: 1.5,
        borderColor: courseTheme.line,
        alignItems: "center",
        justifyContent: "center"
    },
    markSel: { borderColor: courseTheme.teal, backgroundColor: courseTheme.teal },
    markCorrect: { borderColor: courseTheme.green, backgroundColor: courseTheme.green },
    markWrong: { borderColor: courseTheme.red, backgroundColor: courseTheme.red },
    markText: { fontSize: 12, fontWeight: "800", color: courseTheme.slateL },
    markTextActive: { color: "#FFFFFF" },
    optText: { flex: 1, fontSize: 14.5, fontWeight: "600", color: courseTheme.navy },
    optTextWrong: { color: courseTheme.slateL, textDecorationLine: "line-through" },
    explain: { marginTop: 14, borderRadius: 14, padding: 14 },
    explainGood: { backgroundColor: courseTheme.greenTint },
    explainBad: { backgroundColor: courseTheme.redTint },
    explainText: { fontSize: 13.5, lineHeight: 20, color: courseTheme.slate },
    tryAgain: { fontSize: 13.5, fontWeight: "700", color: courseTheme.red }
});
